import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createFileRoute, Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { CheckCircle2 } from 'lucide-react';
import { env } from '@/lib/env';
import { company } from '@/lib/company';
import { startCheckout, captureCheckout, fetchHold, formatMoney, fromGuestParty } from '@/lib/booking';
import { apartmentQuery, resolveHouseRules } from '@/lib/apartments';
import { legalDocumentsQuery, hashHouseRulesText, acceptedDocumentsFrom } from '@/lib/legal';
import { partyLabel } from '@/lib/party';
import { buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { HouseRulesAccordion } from '@/components/HouseRulesAccordion';
import { HoldTimer } from '@/components/HoldTimer';
import { PriceBreakdown } from '@/components/PriceBreakdown';
import {
  captureCheckoutStarted,
  captureCheckoutDocumentsChanged,
  captureCheckoutPaymentSuccess,
  captureCheckoutPaymentFailed,
  captureCheckoutAbandoned,
  capturePaymentMethodSelected,
  captureHoldExpired,
} from '@/lib/analytics';
import { applySeo } from '@/lib/seo';
import { formatCancellationDeadline, isCancellationDeadlineInFuture } from '@/lib/calendar';
import { baseLocale } from '@/paraglide/runtime';
import { m } from '@/paraglide/messages';

// OH-69 task 3: lazy-loaded so react-markdown + remark-gfm only load once a
// guest actually opens the Terms or Cancellation Policy link — see
// LegalDocDialog.tsx's own comment.
const LegalDocDialog = lazy(() =>
  import('@/components/LegalDocDialog').then((mod) => ({ default: mod.LegalDocDialog })),
);

type LegalDocKind = 'terms' | 'cancellation' | 'house_rules';

// Checkout page (/book/$holdId). Renders the PayPal button which:
//   onCreateOrder → POST /holds/{id}/checkout → returns the PayPal order id
//   onApprove     → POST /holds/{id}/capture  → "payment received, finalizing…"
// The booking is created asynchronously (outbox → worker), so we do NOT show a
// booking here — that confirmation/view is sub-slice 4B.
export const Route = createFileRoute('/book/$holdId')({
  component: Checkout,
});

type Phase = 'ready' | 'processing' | 'finalizing' | 'expired' | 'error';

function Checkout() {
  const { holdId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const ctx = location.state;

  // noindex: checkout is a transactional page with a hold-id in the URL.
  useEffect(() => {
    return applySeo({ title: 'Checkout — ollio', noindex: true });
  }, []);

  // One idempotency key per checkout session, stable across button retries.
  const idemKey = useMemo(() => crypto.randomUUID(), []);

  const [phase, setPhase] = useState<Phase>('ready');
  // Refs, not state: the pagehide/unmount handler must read the CURRENT phase
  // and funding source without re-subscribing on every change — a listener
  // that re-registers mid-payment can miss the very exit it exists to catch.
  const phaseRef = useRef<Phase>('ready');
  const methodPickedRef = useRef<string | null>(null);
  const abandonReportedRef = useRef(false);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // GET /holds/{id} (design doc §9.3): location.state (ctx, above) is the
  // instant-render fallback — it's what BookingWidget passes on a normal
  // navigate() and paints with zero network round trip — but a hard reload
  // empties it (that was the actual bug: the total vanished entirely). The
  // hold fetch is the authoritative source once it lands, so it wins over
  // ctx wherever both carry a value. HoldView has no apartment_title (design
  // doc §9.3's DTO is deliberately narrower than the internal Hold schema),
  // so that one field stays ctx-only regardless.
  //
  // Default retry (not `retry: false`): fetchHold only throws on a genuine
  // transport/5xx error (a 404 already resolves to `null`, not a throw), and
  // on a cold reload — no ctx fallback at all — a single transient blip
  // should not permanently blank the total for the rest of this page view.
  //
  // staleTime: 0 — overriding lib/queryClient.ts's global 30_000ms default.
  // BookingWidget seeds this exact query on a normal reserve → navigate
  // arrival (see its case 'created': comment) so the first paint here
  // already has the breakdown instead of flashing the plain dl Total row
  // first. Under the app's default staleTime that seeded entry would read
  // as "fresh" for 30s and suppress this query's own fetch entirely — the
  // "server response is authoritative once it lands" comment above would be
  // false for up to 30 seconds after every normal reserve. A hold is
  // short-lived, per-guest, price-bearing state (the server itself marks
  // GET /holds/{id} no-store, §9.3) — it should never be read from a stale
  // client cache, seeded or not. This override makes the seed a pure
  // first-paint optimization: it removes the empty-then-full flash without
  // ever skipping the real fetch.
  const { data: hold, isSuccess: holdFetched } = useQuery({
    queryKey: ['hold', holdId],
    queryFn: () => fetchHold(holdId),
    staleTime: 0,
  });

  // OH-69 fix (found in review while adding the consent gate below): before
  // this task, a definitively-gone hold (404 → fetchHold resolves `null`,
  // covering unknown/expired/consumed alike — see fetchHold's own comment)
  // still let the guest reach the PayPal button, whose click would surface
  // the server's 409/410 and land them on the "Your hold expired" card. The
  // consent checkbox below is now gated on hold?.apartment_id, so that same
  // dead hold instead disables the checkbox forever with no explanation —
  // silent failure-closed, not the honest "start over" this page already
  // has. Route it to the SAME card explicitly. Guarded to `phase === 'ready'`
  // so it can never override an in-flight/completed payment (e.g. a second
  // tab whose hold a first tab just consumed while this one was mid-'processing').
  useEffect(() => {
    if (holdFetched && hold === null && phase === 'ready') {
      setPhase('expired');
    }
  }, [holdFetched, hold, phase]);

  const totalMinor = hold?.total_amount_minor ?? ctx?.holdTotalMinor;
  const currency = hold?.currency ?? ctx?.holdCurrency;
  const checkinDate = hold?.checkin ?? ctx?.checkinDate;
  const checkoutDate = hold?.checkout ?? ctx?.checkoutDate;

  const total = totalMinor != null && currency ? formatMoney(totalMinor, currency) : null;

  // Composition captured at hold creation. Absent means none was recorded
  // (every pre-existing hold, and any hold created before this feature
  // shipped) — the row below renders nothing at all in that case, not an
  // empty row. Sourced only from the hold fetch, never location.state:
  // BookingWidget's navigate({ state }) does not carry guests.
  const partyText = hold?.guests ? partyLabel(fromGuestParty(hold.guests)) : null;


  // Where "pick these dates again" points. hold survives an expiry caused by
  // the timer (the row is still readable); ctx survives a normal navigation.
  // Only a hard reload onto an already-dead hold leaves both empty, and the
  // card falls back to the apartment list.
  const expiredApartmentId = hold?.apartment_id ?? ctx?.apartmentId;
  const expiredReturn = expiredApartmentId
    ? {
        apartmentId: expiredApartmentId,
        search: {
          ...(checkinDate ? { checkin: checkinDate } : {}),
          ...(checkoutDate ? { checkout: checkoutDate } : {}),
        },
      }
    : null;

  // Reported once per deadline by HoldTimer itself; this only forwards it.
  const onHoldExpired = useCallback(() => {
    captureHoldExpired(holdId);
  }, [holdId]);

  // OH-68 §3.5: no booking exists yet at checkout — this is the deadline
  // that WOULD apply under the current standard policy (computed
  // server-side from the hold's apartment/dates), sourced only from the
  // hold fetch/seed, same as partyText above. Absent means it could not be
  // resolved (e.g. a hold created before this feature shipped and never
  // refetched) — the line below renders nothing in that case, never a
  // guessed date. isCancellationDeadlineInFuture guards a near-term stay
  // whose 100%-tier cutoff already fell before today (review round 1,
  // IMPORTANT 1) — the same already-lapsed-deadline false statement, not
  // just an old booking's problem.
  const cancellationDeadlineLabel =
    hold?.free_cancellation && isCancellationDeadlineInFuture(hold.free_cancellation.at)
      ? formatCancellationDeadline(hold.free_cancellation.at, hold.free_cancellation.timezone)
      : null;

  // --- OH-69 task 3: consent ledger, checkout side ---
  //
  // Both queries read from the API — never the SPA's bundled /legal/*
  // markdown — so the terms/cancellation text shown here is the SAME
  // published version whose (kind, version, sha256) the consent row
  // CheckoutHold writes will name (see lib/legal.ts and the openapi
  // description on GET /legal-documents). House rules are per-apartment,
  // already API-sourced via the same apartment-detail endpoint OH-63 uses.
  const apartmentId = hold?.apartment_id;
  const aptQuery = useQuery({
    ...apartmentQuery(apartmentId ?? ''),
    enabled: !!apartmentId,
  });
  const legalDocsQuery = useQuery(legalDocumentsQuery());

  // OH-82: ctx first (it paints before any request resolves on a normal
  // navigation), the fetched apartment second. Before this the title was
  // ctx-only, so a reload left the page addressing nobody's apartment in
  // particular — while this very query already held the name.
  const apartmentTitle = ctx?.apartmentTitle ?? aptQuery.data?.title ?? null;

  const houseRulesText = aptQuery.data ? resolveHouseRules(aptQuery.data) : null;
  const hasHouseRules = houseRulesText !== null;

  // OH-69 fix round 1: the (version, sha256) this checkout page will claim
  // for house_rules, computed client-side (there is no publish step for
  // house rules to read it from a response the way terms/cancellation are).
  // Keyed on houseRulesText so an apartment read that returns DIFFERENT
  // rules (e.g. after documentsChanged forces a refetch below) recomputes
  // rather than reuses a stale hash.
  const houseRulesHashQuery = useQuery({
    queryKey: ['house-rules-hash', houseRulesText],
    queryFn: () => hashHouseRulesText(houseRulesText ?? ''),
    enabled: hasHouseRules,
  });

  // Fail closed on ANY of the three, not just legalDocsQuery: an apartment
  // read that errors, or a house-rules hash that hasn't finished computing,
  // must not silently degrade the consent copy / accepted_documents to "no
  // house rules" while CheckoutHold's own (fresh, server-side) apartment
  // lookup still finds and records them — a mismatch between what the
  // guest saw and what the ledger names is exactly the failure this task
  // closes.
  const houseRulesHashSettled = !hasHouseRules || houseRulesHashQuery.isSuccess;
  const documentsReady = legalDocsQuery.isSuccess && aptQuery.isSuccess && houseRulesHashSettled;
  const documentsUnavailable =
    legalDocsQuery.isError || aptQuery.isError || (hasHouseRules && houseRulesHashQuery.isError);

  // What this checkout POST will claim it displayed — additive alongside
  // `accepted`, never a substitute for it (spec §7 criterion 9 is still the
  // checkbox). Recomputed whenever the underlying documents/hash change, so
  // a documents_changed-triggered refetch produces a fresh claim once the
  // guest re-ticks, not the stale one that was just refused.
  const acceptedDocuments = useMemo(
    () => acceptedDocumentsFrom(legalDocsQuery.data, hasHouseRules ? houseRulesHashQuery.data : undefined),
    [legalDocsQuery.data, hasHouseRules, houseRulesHashQuery.data],
  );

  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState(false);
  const [openDoc, setOpenDoc] = useState<'terms' | 'cancellation' | null>(null);
  const [houseRulesOpen, setHouseRulesOpen] = useState(false);
  const houseRulesRef = useRef<HTMLDetailsElement>(null);

  // OH-69 fix round 2 (finding A): re-arm the tick against whatever
  // acceptedDocuments CURRENTLY resolves to, not just what it resolved to
  // at tick time. PayPal's flow opens a popup — window-focus return (after
  // approval, cancel, or just tabbing back) is the NORMAL path here, and
  // legalDocsQuery/aptQuery's default staleTime + refetchOnWindowFocus can
  // land a fresher terms/cancellation/house-rules read right then. Without
  // this, the checkbox could stay ticked against version A while
  // acceptedDocuments silently recomputed to version B underneath it, and
  // the ledger would end up recording B for a tick the guest only ever
  // gave for A — the same class of gap the 409 documents_changed path
  // closes, just reachable without a round trip to the server. JSON keying
  // (not object identity) so a refetch that returns IDENTICAL content
  // never spuriously unticks — acceptedDocumentsFrom always builds
  // [terms, cancellation, house_rules?] in that fixed order, so the string
  // is a stable, order-independent-in-practice fingerprint of "what would
  // be sent right now."
  const acceptedDocumentsKey = JSON.stringify(acceptedDocuments);
  const acceptedDocumentsKeyRef = useRef(acceptedDocumentsKey);
  useEffect(() => {
    if (acceptedDocumentsKeyRef.current === acceptedDocumentsKey) return;
    acceptedDocumentsKeyRef.current = acceptedDocumentsKey;
    if (accepted) {
      setAccepted(false);
      setErrorMsg(m.checkout_documents_changed());
    }
  }, [acceptedDocumentsKey, accepted]);

  const onOpenDoc = useCallback((kind: LegalDocKind) => {
    if (kind === 'house_rules') {
      setHouseRulesOpen(true);
      // Best-effort: jsdom (tests) has no scrollIntoView; guard rather than
      // throw. Real browsers always have it.
      if (typeof houseRulesRef.current?.scrollIntoView === 'function') {
        houseRulesRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      return;
    }
    setOpenDoc(kind);
  }, []);

  // onCreateOrder: ask our server to create (or reuse) the PayPal order.
  const onCreateOrder = useCallback(async (): Promise<string> => {
    setErrorMsg(null);
    const res = await startCheckout(holdId, idemKey, accepted, acceptedDocuments, baseLocale);
    if (res.kind === 'ready') {
      captureCheckoutStarted(holdId);
      // OH-82: the server just extended this hold (checkout.go calls
      // hold.ExtendForCheckout, DefaultTTL 15m → CheckoutTTL 25m). Without
      // this refetch the badge keeps counting down the OLD deadline and
      // reaches zero while the guest is entering card details — telling them
      // their dates are gone at the exact moment the hold is safest.
      void queryClient.invalidateQueries({ queryKey: ['hold', holdId] });
      return res.intent.orderId;
    }
    if (res.kind === 'documents_changed') {
      // OH-69 fix round 1: NOT an error state — the hold is still fine,
      // only what the guest ticked no longer matches what the server now
      // requires (a republish, or a house-rules edit, since this page
      // loaded). Refetch + re-tick, never blocked. Invalidating
      // ['house-rules-hash', ...] isn't needed separately: it's keyed on
      // houseRulesText, which recomputes once the apartment refetch lands.
      captureCheckoutDocumentsChanged(holdId);
      setAccepted(false);
      void queryClient.invalidateQueries({ queryKey: ['legal-documents'] });
      if (apartmentId) {
        void queryClient.invalidateQueries({ queryKey: ['public-apartment', apartmentId] });
      }
      setErrorMsg(m.checkout_documents_changed());
      throw new Error('documents_changed');
    }
    if (res.kind === 'expired') {
      setPhase('expired');
      throw new Error('hold_expired');
    }
    if (res.kind === 'unavailable') {
      setPhase('error');
      setErrorMsg('Checkout is temporarily unavailable. Please try again later.');
      throw new Error('checkout_unavailable');
    }
    if (res.kind === 'server_error') {
      // OH-148: our own failure, and one that a retry cannot clear — the
      // prod incident had the owner retry twenty times against rejected
      // PayPal credentials. Say whose fault it is, say no money moved, and
      // point at the one action that does work: talking to us.
      setPhase('error');
      setErrorMsg(m.checkout_payment_unavailable());
      throw new Error('checkout_server_error');
    }
    setPhase('error');
    setErrorMsg('Could not start payment. Please try again.');
    throw new Error('checkout_error');
  }, [holdId, idemKey, accepted, acceptedDocuments, apartmentId, queryClient]);

  // onApprove: capture server-side. Booking finalizes asynchronously.
  const onApprove = useCallback(async (): Promise<void> => {
    setPhase('processing');
    const res = await captureCheckout(holdId, idemKey);
    switch (res.kind) {
      case 'captured':
      case 'pending':
        captureCheckoutPaymentSuccess(holdId);
        // With a confirmation token, go to /confirmation (polls for the
        // async-created booking + shows the full confirmation). Without one
        // (booking-view tokens unconfigured), stay on the inline finalizing
        // screen — the guest still gets the email confirmation.
        if (res.confirmationToken) {
          void navigate({ to: '/confirmation', search: { token: res.confirmationToken } });
          return;
        }
        setPhase('finalizing');
        return;
      case 'expired':
        setPhase('expired');
        return;
      default:
        captureCheckoutPaymentFailed(holdId);
        setPhase('error');
        setErrorMsg('We could not confirm your payment. If you were charged, it will be refunded.');
    }
  }, [holdId, idemKey, navigate]);

  // onError fires for genuine SDK failures (popup blocked, script error) AND
  // for every rejection thrown out of onCreateOrder — the SDK cannot tell the
  // two apart. So it must never overwrite a message that path already wrote:
  // in OH-148 it replaced "PayPal rejected our credentials"-grade information
  // with a generic apology, and the diagnosis had to come from prod logs
  // instead of from the screen. It speaks only when nothing else has.
  const onError = useCallback(() => {
    captureCheckoutPaymentFailed(holdId, 'sdk_error');
    setPhase((prev) => (prev === 'ready' ? 'error' : prev));
    setErrorMsg((prev) => prev ?? m.checkout_paypal_error());
  }, [holdId]);

  const onPaymentMethodClick = useCallback(
    (method: string) => {
      methodPickedRef.current = method;
      capturePaymentMethodSelected(holdId, method);
    },
    [holdId],
  );

  // checkout.abandoned — the CLIENT-SIDE half (OH-71). Best-effort by nature:
  // a closed laptop or a killed tab sends nothing, which is why the reliable
  // half is server-side (a hold that expires unpaid) and reported separately.
  // `pagehide` rather than `beforeunload`: it also fires when a mobile browser
  // backgrounds the tab, and it does not block navigation.
  //
  // `stage` says how far they got, because "left before touching PayPal" and
  // "left with the card form open" are different problems.
  useEffect(() => {
    const report = () => {
      if (abandonReportedRef.current) return;
      // Paying, paid, or already finished: not an abandonment.
      if (phaseRef.current !== 'ready') return;
      abandonReportedRef.current = true;
      captureCheckoutAbandoned(holdId, methodPickedRef.current ?? 'before_payment_method');
    };
    window.addEventListener('pagehide', report);
    return () => {
      window.removeEventListener('pagehide', report);
      report(); // leaving this route counts too, not just leaving the site
    };
  }, [holdId]);

  return (
    <main className="mx-auto w-full max-w-xl flex-1 px-6 py-12">
      {phase === 'finalizing' ? (
        <StatusCard
          icon={<CheckCircle2 className="h-8 w-8 text-[#7fd6a0]" aria-hidden="true" />}
          title="Payment received"
          body="We're finalizing your booking now. You'll receive a confirmation by email shortly."
        />
      ) : phase === 'expired' ? (
        <StatusCard
          title="Your hold expired"
          body="The reservation window closed before payment completed. The dates may well still be free — pick them again to carry on."
          action={
            // OH-82: the card used to say "start over" and stop there,
            // leaving the guest to find the apartment again by hand and
            // retype dates they had already chosen. The ids and dates are
            // right here; the only reason not to offer the link was that
            // nobody wrote it. Falls back to the apartment list when a hard
            // reload left us without either (a 404 hold carries nothing).
            expiredReturn ? (
              <Link
                to="/apartment/$id"
                params={{ id: expiredReturn.apartmentId }}
                search={expiredReturn.search}
                className={buttonVariants({ variant: 'primary', size: 'md' })}
              >
                Pick these dates again
              </Link>
            ) : (
              <Link to="/" className={buttonVariants({ variant: 'primary', size: 'md' })}>
                Browse apartments
              </Link>
            )
          }
        />
      ) : (
        <div className="rounded-[var(--radius-2xl)] border border-[var(--border-hairline)] bg-card p-7 shadow-[var(--shadow-lg)]">
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-[var(--ollio-ink)]">
            Complete your booking
          </h1>
          {/* OH-82: ctx is empty after a reload, but the apartment itself is
              already fetched here (aptQuery, for house rules) — so the title
              is sitting in memory while the page renders a nameless "Complete
              your booking". Prefer ctx only because it paints one round trip
              sooner on a normal navigation. */}
          {apartmentTitle ? (
            <p className="mt-1.5 font-serif text-xl font-semibold text-[var(--ollio-ink)]">
              {apartmentTitle}
            </p>
          ) : null}

          <HoldTimer expiresAt={hold?.expires_at} onExpire={onHoldExpired} />

          <dl className="mt-6 space-y-2 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-4 py-3 text-sm">
            {checkinDate ? (
              <div className="flex items-baseline justify-between">
                <dt className="text-muted">Check-in</dt>
                <dd className="font-medium text-[var(--ollio-ink)]">{checkinDate}</dd>
              </div>
            ) : null}
            {checkoutDate ? (
              <div className="flex items-baseline justify-between">
                <dt className="text-muted">Check-out</dt>
                <dd className="font-medium text-[var(--ollio-ink)]">{checkoutDate}</dd>
              </div>
            ) : null}
            {partyText ? (
              <div className="flex items-baseline justify-between" data-testid="guest-party">
                <dt className="text-muted">Guests</dt>
                <dd className="font-medium text-[var(--ollio-ink)]">{partyText}</dd>
              </div>
            ) : null}
            {/* Only when there's no breakdown to show instead (below) — see
                that block's comment for why the total must never appear
                twice on this page. Absent a breakdown, this row is
                UNCHANGED from before this task: same condition (`total`),
                same position inside this dl, same markup. That's the
                "degrades to exactly today's rendering" case (design doc
                §11) for the common path — OTA/manual bookings never reach
                this screen, but a deploy-window hold created before the
                migration and captured after it can still lack one. */}
            {!hold?.price_breakdown && total ? (
              <div className="flex items-baseline justify-between">
                <dt className="text-muted">Total</dt>
                <dd className="font-mono text-lg font-semibold tabular-nums text-[var(--ollio-ink)]">
                  {total}
                </dd>
              </div>
            ) : null}
            <div className="flex items-baseline justify-between">
              <dt className="text-muted">Hold reference</dt>
              <dd className="break-all font-mono text-xs text-muted">{holdId}</dd>
            </div>
          </dl>

          {/* Above the pay button (design doc §11): the full total must be
              visible before payment details — that's the Baymard finding
              this whole issue rests on. Rendered only once the hold fetch
              lands with a breakdown, and when it does, the dl's own Total
              row above is suppressed (its `!hold?.price_breakdown` guard) so
              the guest sees the total exactly once, right before paying,
              not the same figure twice. */}
          {hold?.price_breakdown ? (
            <div className="mt-6">
              <PriceBreakdown
                lineItems={hold.price_breakdown.line_items}
                nights={hold.price_breakdown.nights}
                totalMinor={hold.price_breakdown.total_amount_minor}
                currency={hold.price_breakdown.currency}
              />
            </div>
          ) : null}

          {/* OH-70: CRD Art 6(1)(e) — the total shown must be stated as final
              and inclusive of applicable taxes. PriceBreakdown already
              itemizes and totals; this restates the guarantee, it never
              recomputes or duplicates the figure.

              Deliberately OUTSIDE the breakdown branch: a hold created before
              the price-breakdown migration and captured after it renders its
              total in the <dl> above instead, and Art 6(1)(e) does not care
              which of the two paths drew the number. Written as
              "breakdown OR total" rather than nesting under either, so the
              statement appears exactly once and only when a price is on
              screen at all. */}
          {hold?.price_breakdown || total ? (
            <p className="mt-2 text-xs text-muted">{m.checkout_price_includes_taxes()}</p>
          ) : null}

          {/* OH-69 §5.7/§7 criterion 8: compact, collapsed accordion — only
              when the apartment actually has house rules. The consent
              checkbox's "House Rules" link (below) opens THIS element
              rather than a second copy of the text. */}
          {hasHouseRules && houseRulesText ? (
            <div className="mt-6">
              <HouseRulesAccordion
                ref={houseRulesRef}
                id="checkout-house-rules"
                text={houseRulesText}
                open={houseRulesOpen}
                onToggle={(e) => setHouseRulesOpen(e.currentTarget.open)}
              />
            </div>
          ) : null}

          {/* OH-69 §5.6/§7 criterion 9: unticked by default (useState(false)
              above); the pay control below stays locked until this is
              checked. Disabled while the API-sourced documents are loading
              or unavailable — fail closed, matching CheckoutHold's own 503
              (spec §3: "the guest must not be able to accept"). */}
          <ConsentSection
            accepted={accepted}
            onAcceptedChange={setAccepted}
            documentsReady={documentsReady}
            documentsUnavailable={documentsUnavailable}
            hasHouseRules={hasHouseRules}
            onOpenDoc={onOpenDoc}
          />

          {/* OH-70: Art 8(2) requires the main characteristics, price, and
              contract duration DIRECTLY before the order button — the
              cancellation-deadline line (contract duration/terms) moved up
              from below the pay control to satisfy that, wording unchanged.
              The obligation-to-pay line beneath it is the substance behind
              Art 8(2)'s "unambiguous obligation to pay" requirement: once
              `accepted` is true, the actual pay control below is PayPal's
              own SDK-rendered button, whose label we do not control. */}
          {cancellationDeadlineLabel ? (
            <p className="mt-5 text-center text-xs text-muted">
              {m.cancellation_deadline({ date: cancellationDeadlineLabel })}
            </p>
          ) : null}

          <p className="mt-3 text-center text-xs text-muted">{m.checkout_obligation_to_pay()}</p>

          {/* OH-82: no currency, no button. The `?? 'EUR'` that used to stand
              here was a wrong answer to a real problem — on a hard reload the
              currency is genuinely unknown for one round trip, and feeding
              the SDK a guess mounts a payment button denominated in a
              currency nobody quoted. (Feeding it `undefined` is worse: PayPal
              falls back to USD.) Waiting is the honest option; it lasts as
              long as one fetch. */}
          <div className="mt-3">
            {currency ? (
            <PayPalArea
              currency={currency}
              onCreateOrder={onCreateOrder}
              onApprove={onApprove}
              onError={onError}
              onPaymentMethodClick={onPaymentMethodClick}
              processing={phase === 'processing'}
              locked={!accepted}
            />
            ) : (
              <p className="text-sm text-muted" data-testid="checkout-loading">
                Loading your reservation…
              </p>
            )}
          </div>

          {errorMsg ? (
            <p role="alert" className="mt-4 text-sm text-destructive">
              {errorMsg}
            </p>
          ) : null}

          {/* OH-70: seller identity + complaints (CRD Art 6(1)(b)/(c)/(h)).
              Deliberately at the BOTTOM of the card, below the pay control —
              it must be on the page but must not push the price/button below
              the fold. Collapsed <details> keeps it out of the way; native
              summary/details semantics make it keyboard-operable for free. */}
          <details
            data-testid="checkout-seller"
            className="mt-6 rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-4 py-3 text-xs text-muted"
          >
            <summary className="cursor-pointer font-medium text-[var(--ollio-ink)]">
              {m.checkout_seller_summary()}
            </summary>
            <div className="mt-3 space-y-1">
              <p className="font-medium text-[var(--ollio-ink)]">{company.name}</p>
              <p>{company.address}</p>
              <p>
                {m.company_registration_label()}: {company.registrationNumber}
              </p>
              <p>
                {m.company_tax_label()}: {company.taxNumber}
              </p>
              <p>
                {m.checkout_seller_bookings_email_label()}: {company.bookingsEmail}
              </p>
              <p>
                {m.company_complaints_label()}: {company.legalEmail}
              </p>
              <p>
                {m.checkout_seller_phone_label()}: {company.phone}
              </p>
              <p className="mt-2">
                {m.checkout_seller_complaints_notice({ email: company.legalEmail })}
              </p>
            </div>
          </details>
        </div>
      )}

      {openDoc ? (
        <Suspense fallback={null}>
          <LegalDocDialog
            title={openDoc === 'terms' ? m.checkout_doc_terms_title() : m.checkout_doc_cancellation_title()}
            content={
              openDoc === 'terms'
                ? (legalDocsQuery.data?.terms.content ?? '')
                : (legalDocsQuery.data?.cancellation.content ?? '')
            }
            onClose={() => setOpenDoc(null)}
          />
        </Suspense>
      ) : null}

      <div className="mt-6 text-center">
        <Link to="/" className={buttonVariants({ variant: 'secondary', pill: true })}>
          Back to apartments
        </Link>
      </div>
    </main>
  );
}

// PayPalArea renders the PayPal button, or a graceful notice when the client id
// is not configured (the operator gate: the button cannot render without it).
//
// OH-69 §5.6/§7 criterion 9: `locked` (driven by the consent checkbox, NOT
// by clientId configuration) swaps ONLY the interactive child — the
// PayPalScriptProvider itself still mounts unconditionally so the PayPal
// SDK preloads while the guest is reading the consent copy, instead of only
// starting to load the moment they tick the box.
function PayPalArea({
  currency,
  onCreateOrder,
  onApprove,
  onError,
  onPaymentMethodClick,
  processing,
  locked,
}: {
  currency: string;
  onCreateOrder: () => Promise<string>;
  onApprove: () => Promise<void>;
  onError: () => void;
  onPaymentMethodClick: (method: string) => void;
  processing: boolean;
  locked: boolean;
}) {
  const clientId = env.paypalClientId;
  if (!clientId) {
    return (
      <div className="rounded-[var(--radius-control)] border border-[var(--ollio-gold)] bg-[rgba(197,165,90,0.1)] px-4 py-3 text-sm text-[var(--ollio-gold-deep)]">
        Online payment is not configured yet. Please contact us to complete your booking.
      </div>
    );
  }

  return (
    <PayPalScriptProvider options={{ clientId, currency, intent: 'capture' }}>
      {processing ? (
        <p className="text-sm text-muted">Processing your payment…</p>
      ) : locked ? (
        <button
          type="button"
          disabled
          aria-disabled="true"
          data-testid="pay-locked-button"
          className="flex h-11 w-full cursor-not-allowed items-center justify-center rounded-full bg-[var(--surface-sunken)] font-semibold text-muted opacity-60"
        >
          {m.checkout_pay_button()}
        </button>
      ) : (
        <PayPalButtons
          style={{ layout: 'vertical' }}
          // onClick fires before the order is created, so it records the
          // funding source a guest actually reaches for (paypal, card, …)
          // even when they then abandon. That distribution is the evidence
          // OH-83 (card fields without a PayPal account) needs, one way or
          // the other.
          onClick={(data) => {
            onPaymentMethodClick(String(data.fundingSource ?? 'unknown'));
          }}
          createOrder={() => onCreateOrder()}
          onApprove={() => onApprove()}
          onError={() => onError()}
        />
      )}
    </PayPalScriptProvider>
  );
}

// ConsentSection: the OH-69 checkout-time acceptance. Starts unticked
// (Checkout's useState(false)); disabled entirely until the API-sourced
// terms/cancellation/apartment reads all succeed, so ticking is physically
// impossible while "can this guest even see what they'd be accepting?" is
// unresolved or false.
function ConsentSection({
  accepted,
  onAcceptedChange,
  documentsReady,
  documentsUnavailable,
  hasHouseRules,
  onOpenDoc,
}: {
  accepted: boolean;
  onAcceptedChange: (v: boolean) => void;
  documentsReady: boolean;
  documentsUnavailable: boolean;
  hasHouseRules: boolean;
  onOpenDoc: (kind: LegalDocKind) => void;
}) {
  return (
    <div className="mt-6">
      <Checkbox
        id="checkout-consent-accept"
        checked={accepted}
        disabled={!documentsReady}
        onChange={(e) => onAcceptedChange(e.target.checked)}
        label={
          <ConsentLabel hasHouseRules={hasHouseRules} clickable={documentsReady} onOpenDoc={onOpenDoc} />
        }
      />
      {documentsUnavailable ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {m.checkout_consent_unavailable()}
        </p>
      ) : null}
      {/* OH-70: CRD Art 16(l) — accommodation booked for specific dates is
          exempt from the statutory 14-day withdrawal right; our own
          Cancellation Policy governs instead. Reuses the SAME link text/
          click target (onOpenDoc('cancellation')) the consent sentence
          above already wires, rather than a second copy of that plumbing. */}
      <p className="mt-2 text-xs text-muted">
        {m.checkout_no_withdrawal_prefix()}{' '}
        <DocLink clickable={documentsReady} onClick={() => onOpenDoc('cancellation')}>
          {m.checkout_consent_cancellation_link()}
        </DocLink>{' '}
        {m.checkout_no_withdrawal_suffix()}
      </p>
    </div>
  );
}

// ConsentLabel composes the checkbox sentence from individually-localized
// fragments (paraglide has no rich-text/markup interpolation, only `{var}`
// placeholders — see project.inlang settings, "en" is the only locale
// today) so "Booking Terms" / "House Rules" / "Cancellation Policy" can each
// be their own clickable link — spec §5.6: "links inline, each document
// reachable in one click". The House Rules segment only appears when the
// apartment has any (spec §7 criterion 8: never claim acceptance of a
// document that doesn't exist).
function ConsentLabel({
  hasHouseRules,
  clickable,
  onOpenDoc,
}: {
  hasHouseRules: boolean;
  clickable: boolean;
  onOpenDoc: (kind: LegalDocKind) => void;
}) {
  return (
    <span>
      {m.checkout_consent_prefix()}{' '}
      <DocLink clickable={clickable} onClick={() => onOpenDoc('terms')}>
        {m.checkout_consent_terms_link()}
      </DocLink>
      {hasHouseRules ? (
        <>
          {', '}
          <DocLink clickable={clickable} onClick={() => onOpenDoc('house_rules')}>
            {m.checkout_consent_house_rules_link()}
          </DocLink>
        </>
      ) : null}{' '}
      {m.checkout_consent_and()}{' '}
      <DocLink clickable={clickable} onClick={() => onOpenDoc('cancellation')}>
        {m.checkout_consent_cancellation_link()}
      </DocLink>
      .
    </span>
  );
}

// DocLink is a button, not an <a>, nested inside the Checkbox's own
// <label> — clicking it must open a document, NOT toggle the checkbox the
// label is associated with. stopPropagation prevents the click event from
// ever reaching the <label> element, which is what the browser's native
// label→control forwarding relies on (see the Checkbox test's identical
// assertion). `clickable=false` (documents not yet resolved) renders plain,
// non-interactive text instead — there is nothing to open yet.
function DocLink({
  clickable,
  onClick,
  children,
}: {
  clickable: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  if (!clickable) {
    return <span>{children}</span>;
  }
  return (
    <button
      type="button"
      className="underline decoration-dotted underline-offset-2 hover:text-[var(--ollio-navy)]"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

function StatusCard({
  icon,
  title,
  body,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-2xl)] border border-[var(--border-hairline)] bg-card p-8 text-center shadow-[var(--shadow-lg)]">
      {icon ? (
        <div className="mx-auto mb-5 flex h-[60px] w-[60px] items-center justify-center rounded-full border-2 border-[var(--ollio-success)] bg-[rgba(47,125,82,0.22)]">
          {icon}
        </div>
      ) : null}
      <h1 className="font-serif text-2xl font-semibold tracking-tight text-[var(--ollio-ink)]">
        {title}
      </h1>
      <p className="mt-2 text-muted">{body}</p>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}
